import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AlertType, SeverityLevel } from '@/constants/alertTypes'
import type { MenuVO } from '@/types/menu'
import type { AuthUserInfo } from '@/types/auth'
import Cookies from 'js-cookie'
import {
  AUTH_TOKEN_COOKIE_KEY,
  AUTH_USER_ID_COOKIE_KEY,
  AUTH_USER_INFO_STORAGE_KEY,
  clearAuthStorage,
  getStoredToken,
  isLikelyJwtToken,
} from '@/utils/auth'

// 用户状态
export const useUserStore = defineStore('user', () => {
  // State
  const persistedToken = getStoredToken()
  if (persistedToken && !isLikelyJwtToken(persistedToken)) {
    clearAuthStorage()
  }

  const token = ref<string>(isLikelyJwtToken(persistedToken) ? persistedToken : '')
  const userInfo = ref<AuthUserInfo | null>(null)

  const permissions = ref<string[]>([])
  const menus = ref<MenuVO[]>([])
  const dynamicRoutesLoaded = ref(false)

  try {
    const rawUserInfo = localStorage.getItem(AUTH_USER_INFO_STORAGE_KEY)
    if (rawUserInfo) {
      userInfo.value = JSON.parse(rawUserInfo)
    }
  } catch {
    localStorage.removeItem(AUTH_USER_INFO_STORAGE_KEY)
  }

  // Getters
  const isLoggedIn = computed(() => !!token.value)
  const username = computed(() => userInfo.value?.username || '')
  const userId = computed(() => userInfo.value?.id || Cookies.get(AUTH_USER_ID_COOKIE_KEY) || '')

  // Actions
  const setToken = (newToken: string) => {
    token.value = newToken
    if (newToken) {
      Cookies.set(AUTH_TOKEN_COOKIE_KEY, newToken, { sameSite: 'lax' })
    } else {
      Cookies.remove(AUTH_TOKEN_COOKIE_KEY)
    }
  }

  const setUserInfo = (info: typeof userInfo.value) => {
    userInfo.value = info
    if (info) {
      Cookies.set(AUTH_USER_ID_COOKIE_KEY, String(info.id), { sameSite: 'lax' })
      localStorage.setItem(AUTH_USER_INFO_STORAGE_KEY, JSON.stringify(info))
    } else {
      Cookies.remove(AUTH_USER_ID_COOKIE_KEY)
      localStorage.removeItem(AUTH_USER_INFO_STORAGE_KEY)
    }
  }

  const setPermissions = (perms: string[]) => {
    permissions.value = perms
  }

  const setMenus = (menuTree: MenuVO[]) => {
    menus.value = menuTree
  }

  /**
   * 需要 keep-alive 缓存的页面组件名列表。
   * 组件名取自菜单 component 字段的最后一段（即 SFC 文件名），
   * Vue 的 <keep-alive :include> 按组件 name 匹配（<script setup> 会自动推断文件名作为 name）。
   */
  const cachedViews = computed(() => {
    const names: string[] = []
    const seen = new Set<string>()
    const walk = (nodes: MenuVO[]) => {
      for (const node of nodes) {
        if (node.type === 'C' && node.keepAlive === 1 && node.component) {
          const name = node.component.split('/').pop()
          if (name && !seen.has(name)) {
            seen.add(name)
            names.push(name)
          }
        }
        if (node.children && node.children.length > 0) {
          walk(node.children)
        }
      }
    }
    walk(menus.value)
    return names
  })

  const setDynamicRoutesLoaded = (loaded: boolean) => {
    dynamicRoutesLoaded.value = loaded
  }

  const logout = () => {
    token.value = ''
    userInfo.value = null
    permissions.value = []
    menus.value = []
    dynamicRoutesLoaded.value = false
    clearAuthStorage()
  }

  return {
    token,
    userInfo,
    permissions,
    menus,
    cachedViews,
    dynamicRoutesLoaded,
    isLoggedIn,
    username,
    userId,
    setToken,
    setUserInfo,
    setPermissions,
    setMenus,
    setDynamicRoutesLoaded,
    logout,
  }
})

// 告警状态
export const useAlertStore = defineStore('alert', () => {
  // State
  const unreadCount = ref(0)
  const latestAlerts = ref<Array<{
    id: string
    type: AlertType
    severity: SeverityLevel
    title: string
    createdAt: string
  }>>([])

  // Getters
  const hasUnread = computed(() => unreadCount.value > 0)

  // Actions
  const setUnreadCount = (count: number) => {
    unreadCount.value = count
  }

  const addAlert = (alert: typeof latestAlerts.value[0]) => {
    latestAlerts.value.unshift(alert)
    unreadCount.value++
  }

  const markAsRead = (alertId: string) => {
    const index = latestAlerts.value.findIndex(a => a.id === alertId)
    if (index !== -1) {
      latestAlerts.value.splice(index, 1)
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    }
  }

  const clearAll = () => {
    latestAlerts.value = []
    unreadCount.value = 0
  }

  return {
    unreadCount,
    latestAlerts,
    hasUnread,
    setUnreadCount,
    addAlert,
    markAsRead,
    clearAll,
  }
})

// ASIN状态
export const useAsinStore = defineStore('asin', () => {
  // State
  const monitoringCount = ref(0)
  const totalCount = ref(0)
  const alertCount = ref(0)

  // Getters
  const pausedCount = computed(() => totalCount.value - monitoringCount.value)

  // Actions
  const setCounts = (data: {
    total: number
    monitoring: number
    alert: number
  }) => {
    totalCount.value = data.total
    monitoringCount.value = data.monitoring
    alertCount.value = data.alert
  }

  return {
    monitoringCount,
    totalCount,
    alertCount,
    pausedCount,
    setCounts,
  }
})

// 应用状态
export const useAppStore = defineStore('app', () => {
  // State
  const sidebarCollapsed = ref(false)
  const theme = ref<'light' | 'dark'>('light')
  const language = ref('zh-CN')

  // Getters
  const isDark = computed(() => theme.value === 'dark')

  // Actions
  const toggleSidebar = () => {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }

  const setTheme = (newTheme: 'light' | 'dark') => {
    theme.value = newTheme
  }

  const setLanguage = (lang: string) => {
    language.value = lang
  }

  return {
    sidebarCollapsed,
    theme,
    language,
    isDark,
    toggleSidebar,
    setTheme,
    setLanguage,
  }
})

// 监控规则状态
export const useRuleStore = defineStore('rule', () => {
  // State
  const rules = ref<Array<{
    id: string
    name: string
    type: AlertType
    enabled: boolean
  }>>([])

  // Getters
  const enabledRules = computed(() => rules.value.filter(r => r.enabled))
  const enabledCount = computed(() => enabledRules.value.length)

  // Actions
  const setRules = (newRules: typeof rules.value) => {
    rules.value = newRules
  }

  const updateRuleStatus = (ruleId: string, enabled: boolean) => {
    const rule = rules.value.find(r => r.id === ruleId)
    if (rule) {
      rule.enabled = enabled
    }
  }

  return {
    rules,
    enabledRules,
    enabledCount,
    setRules,
    updateRuleStatus,
  }
})

// 反制策略状态
export const useCounterStore = defineStore('counter', () => {
  // State
  const strategies = ref<Array<{
    id: string
    name: string
    type: string
    enabled: boolean
  }>>([])

  // Getters
  const enabledStrategies = computed(() => strategies.value.filter(s => s.enabled))
  const autoCounterEnabled = computed(() => enabledStrategies.value.length > 0)

  // Actions
  const setStrategies = (newStrategies: typeof strategies.value) => {
    strategies.value = newStrategies
  }

  const toggleStrategy = (strategyId: string, enabled: boolean) => {
    const strategy = strategies.value.find(s => s.id === strategyId)
    if (strategy) {
      strategy.enabled = enabled
    }
  }

  return {
    strategies,
    enabledStrategies,
    autoCounterEnabled,
    setStrategies,
    toggleStrategy,
  }
})

export default {
  useUserStore,
  useAlertStore,
  useAsinStore,
  useAppStore,
  useRuleStore,
  useCounterStore,
}
